import React, { useEffect, useState } from "react"
import { ICustomTransformStep, IQuery, IQueryBuilderContext, IStepValidationResponse, StepType } from "@types"
import { findSourceById, getSelectedStepDetailsById, handleStepFieldValueChanges, registerStep, validateSource, validateStep, validateStepNameAndId } from "@utils"
import { FormSection } from "@components/FormSection/FormSection"
import { SourceSelectorInput } from "../SourceSelector/SourceSelectorInput"
import { AsyncButton, hasValue, TextArea } from "uxp/components"
import { getCustomTransformation } from "@query-engine-service"
import Icon from '@images/transform.svg'
import Copilot from '@images/copilot.svg'
import { trackAnalytics } from "../../../analytics";


export function renderStep(context: IQueryBuilderContext, query: IQuery, onChange: (query: IQuery) => void, selectedStepId: string) {
    const { step, index, previousSteps, selectedSources } = getSelectedStepDetailsById(query, selectedStepId)
    const _step: ICustomTransformStep = Object.assign({}, step as ICustomTransformStep)

    const [errors, setErrors] = useState<Record<string, string>>({})

    const [desc, setDesc] = useState('');

    useEffect(() => {
        const { isValid, errors } = validateStep(query, step as ICustomTransformStep)
        setErrors(errors)
        setDesc((step as ICustomTransformStep)?.description || '')
    }, [step])

    async function onValueChange(field: string, value: any, source?: boolean) {
        const _query = await handleStepFieldValueChanges(context, query, selectedStepId, field, value, source)
        onChange(_query)
    }


    const source = findSourceById(query, _step?.source)

    return <React.Fragment>

        <FormSection
            label='Source'
            error={errors?.source}
        >
            <SourceSelectorInput
                source={source}
                onChange={(newSource) => { onValueChange('source', newSource, true) }}
                availableSources={[...previousSteps, ...selectedSources]}
                query={query}
            />
        </FormSection>

        <FormSection
            label='Description'
            error={errors?.description}
            labelPosition="top"
        >
            <TextArea

                value={desc || ''}
                onChange={(s) => { setDesc(s) }}
            // onChange={(s) => { onValueChange('description', s) }}
            />
            <div
                title={_step.code}
                style={{
                    'display': 'flex',
                    'alignItems': 'flex-start',
                    'paddingTop': '10px',
                    'justifyContent': 'flex-end',
                }}>

                {(!_step.code || (desc != _step.description)) &&
                    <AsyncButton
                        icon={Copilot}
                        className="copilot-button"
                        onClick={async () => {
                            let r = await getCustomTransformation(context, desc);
                            trackAnalytics(context.uxpContext, 'custom-transform', { message: desc, success: true });

                            onValueChange('description', desc);
                            onValueChange('code', r.code);
                            onValueChange('added', r.added || []);
                            onValueChange('removed', r.removed || []);


                        }} title="Apply" />}
            </div>
            <div className='lucy-query-editor-info' style={{ maxWidth: '600px' }}>

                Examples:
                <ul>
                    <li>Convert the 'Value' field to a number</li>
                    <li>Take the domain portion of the 'Email' field and add it as a new field called 'Domain'</li>
                    <li>Remove all '$' symbols from the Currency field and convert it to a number</li>
                </ul>
                Feel free to give examples when you describe your transformation:
                <ul>
                    <li>add a new 'TimeSinceLastUpdate' field that has a friendly label showing how much time has passed since the timestamp. Example: '45min ago' or '2 days ago'</li>
                </ul>
            </div>

        </FormSection>


    </React.Fragment>
}

function validate(query: IQuery, step: ICustomTransformStep): IStepValidationResponse {
    const errors: Record<string, string> = validateStepNameAndId(step);
    // validate source
    const source = validateSource(query, step, 'source')
    if (!source.isValid) errors.source = source.error
    if (!hasValue(step.description)) errors.description = "Describe the transformation you want";
    if (!hasValue(step.code)) errors.code = 'Apply your changes to see them';
    return { isValid: Object.keys(errors).length === 0, errors };
}

registerStep({
    type: StepType.CustomTransform,
    name: "Custom Transform",
    description: "Transform data, add/modify columns by applying custom AI-generated logic based on what you describe",
    icon: Icon,
    render: renderStep,
    validate: validate,
    onSelectStep: (basicProps, previousSource) => {
        return { ...basicProps, source: previousSource, description: '', code: '' } as ICustomTransformStep
    }
})